/**
 * Pure helpers for the printable documents (tracker item 26). Money stays a
 * server decimal string: nothing here adds, rounds or recomputes amounts.
 */

import * as types from '../../api/types';

/** Largest page the reports API returns (report.business.ts caps limit at 100). */
export const STATEMENT_PRINT_PAGE_SIZE = 100;

/** Safety stop for the page loop (100 x 500 = 50,000 lines). */
export const STATEMENT_PRINT_MAX_PAGES = 500;

/** Query flag the Print buttons add so the print route opens the print dialog once. */
export const AUTO_PRINT_PARAM = 'autoprint';

/**
 * Company address as display lines: street lines, then "City, ST 12345".
 * Blank fields are skipped so a partial address never prints empty lines.
 */
export function companyAddressLines(settings: Pick<types.CompanySettings, 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'postalCode'>): string[] {
  const cityState = [settings.city, settings.state].map((part) => part?.trim()).filter(Boolean).join(', ');
  const locality = [cityState, settings.postalCode?.trim()].filter(Boolean).join(' ');
  return [settings.addressLine1, settings.addressLine2, locality].map((line) => line?.trim() || '').filter(Boolean);
}

/** Customer address as display lines (same rules as companyAddressLines). */
export function customerAddressLines(customer: Pick<types.Customer, 'address' | 'city' | 'state' | 'zip'>): string[] {
  return companyAddressLines({ addressLine1: customer.address ?? null, addressLine2: null, city: customer.city ?? null, state: customer.state ?? null, postalCode: customer.zip ?? null });
}

/** Phone, email and website on one line, separated by " · ". */
export function companyContactLine(settings: Pick<types.CompanySettings, 'phone' | 'email' | 'website'>): string {
  return [settings.phone, settings.email, settings.website].map((part) => part?.trim()).filter(Boolean).join(' · ');
}

export interface DocumentMarkInfo {
  label: 'VOID' | 'DRAFT';
  note: string;
}

/** The stamp a printed invoice carries: VOID and DRAFT invoices are marked, others are not. */
export function documentMarkFor(status: types.InvoiceStatus | string): DocumentMarkInfo | null {
  if (status === types.InvoiceStatus.VOID) return { label: 'VOID', note: 'This invoice has been cancelled. No payment is due.' };
  if (status === types.InvoiceStatus.DRAFT) return { label: 'DRAFT', note: 'Draft: this invoice has not been sent.' };
  return null;
}

/** Print route for an invoice, job or statement, with the auto-print flag. */
export function invoicePrintPath(invoiceId: string): string {
  return `/print/invoices/${encodeURIComponent(invoiceId)}?${AUTO_PRINT_PARAM}=1`;
}

export function jobPrintPath(jobId: string): string {
  return `/print/jobs/${encodeURIComponent(jobId)}?${AUTO_PRINT_PARAM}=1`;
}

/**
 * Statement print route. Carries the on-screen date range so the paper copy
 * covers the same period as the screen (the page number is not carried: the
 * print view always holds every line).
 */
export function statementPrintPath(customerId: string, range: { startDate?: string; endDate?: string }): string {
  const query = new URLSearchParams();
  if (range.startDate) query.set('startDate', range.startDate);
  if (range.endDate) query.set('endDate', range.endDate);
  query.set(AUTO_PRINT_PARAM, '1');
  return `/print/customers/${encodeURIComponent(customerId)}/statement?${query.toString()}`;
}

/**
 * Loads every line of a statement by walking `page` until the server says
 * there are no more pages. Opening balance comes from the first page and the
 * closing balance from the last; lines are concatenated in server order, so
 * the running balance column is exactly what the API sent. Any failed page
 * rejects the whole load (no partial statement is ever returned).
 */
export async function fetchAllStatementLines(
  fetchPage: (query: types.CustomerStatementQuery) => Promise<types.CustomerStatementReport>,
  range: { startDate?: string; endDate?: string },
): Promise<types.CustomerStatementReport> {
  const base = { ...(range.startDate ? { startDate: range.startDate } : {}), ...(range.endDate ? { endDate: range.endDate } : {}) };
  const first = await fetchPage({ ...base, page: 1, limit: STATEMENT_PRINT_PAGE_SIZE });
  const lines = [...first.lines];
  let last = first;
  const pages = first.pagination?.pages ?? 1;
  // Refuse rather than print a silently truncated statement.
  if (pages > STATEMENT_PRINT_MAX_PAGES) throw new Error('This statement is too long to print. Choose a shorter date range.');
  for (let page = 2; page <= pages; page += 1) {
    last = await fetchPage({ ...base, page, limit: STATEMENT_PRINT_PAGE_SIZE });
    lines.push(...last.lines);
    if (last.lines.length === 0) break;
  }
  return {
    ...first,
    closingBalance: last.closingBalance,
    lines,
    pagination: { page: 1, limit: lines.length, total: lines.length, pages: 1 },
  };
}
